/**
 * IQ Score — live in-practice intelligence scoring panel.
 *
 * Coach picks rosters for Red vs Black from their team, then taps action
 * buttons every time they see a smart read. The first side to hit the
 * target wins. When the game ends (or the coach taps "Save"), the
 * session — including the player ids on each side and the full action
 * log — is persisted via `/api/iq-score/sessions` so the team can look
 * back at which group reads the game better over time.
 *
 * State lives in this component so the keypad stays instant even on a
 * tablet. The backend save is best-effort and only available to
 * coach/admin (players can still use the tracker locally).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Hand, ArrowDownToLine, Shield, Zap, Users, Repeat2, Target,
  Trophy, RotateCcw, Undo2, Crown, Activity, UserPlus, X, Save,
  Check, History as HistoryIcon, Sparkles,
} from 'lucide-react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

const MODES = ['2v2', '3v3', '4v4', '5v5'];
const TARGETS = [12, 20, 21];

// Single source of truth for actions.
const ACTIONS = [
  { key: 'assist',   labelKey: 'iqs_a_assist',   points: 1.0, iconCmp: Hand,            color: 'cyan' },
  { key: 'rebound',  labelKey: 'iqs_a_rebound',  points: 1.0, iconCmp: ArrowDownToLine, color: 'sky' },
  { key: 'block',    labelKey: 'iqs_a_block',    points: 1.0, iconCmp: Shield,          color: 'indigo' },
  { key: 'steal',    labelKey: 'iqs_a_steal',    points: 1.0, iconCmp: Zap,             color: 'violet' },
  { key: 'screen',   labelKey: 'iqs_a_screen',   points: 1.0, iconCmp: Users,           color: 'amber' },
  { key: 'pnr',      labelKey: 'iqs_a_pnr',      points: 1.5, iconCmp: Repeat2,         color: 'fuchsia' },
  { key: 'bucket',   labelKey: 'iqs_a_bucket',   points: 0.5, iconCmp: Target,          color: 'emerald' },
];

const COMBOS = [
  { key: 'oreb_score',         labelKey: 'iqs_c_oreb_score',         points: 1.5 },
  { key: 'block_pass_bucket',  labelKey: 'iqs_c_block_pass_bucket',  points: 2.0 },
  { key: 'steal_layup',        labelKey: 'iqs_c_steal_layup',        points: 1.5 },
  { key: 'screen_pass_shot',   labelKey: 'iqs_c_screen_pass_shot',   points: 2.0 },
];

const COLOR_TO_TW = {
  cyan: 'from-cyan-500/20 to-cyan-700/20 text-cyan-300 border-cyan-500/40',
  sky: 'from-sky-500/20 to-sky-700/20 text-sky-300 border-sky-500/40',
  indigo: 'from-indigo-500/20 to-indigo-700/20 text-indigo-300 border-indigo-500/40',
  violet: 'from-violet-500/20 to-violet-700/20 text-violet-300 border-violet-500/40',
  amber: 'from-amber-500/20 to-amber-700/20 text-amber-300 border-amber-500/40',
  fuchsia: 'from-fuchsia-500/20 to-fuchsia-700/20 text-fuchsia-300 border-fuchsia-500/40',
  emerald: 'from-emerald-500/20 to-emerald-700/20 text-emerald-300 border-emerald-500/40',
};

const initialTeam = () => ({ points: 0, actions: [] });

// ─── Roster picker modal ─────────────────────────────────────────────
function RosterPicker({ color, roster, takenIds, onPick, onClose, t }) {
  const available = roster.filter((p) => !takenIds.has(p.id));
  // Local multi-select. We commit the whole batch on "Add" so the coach
  // can build a 5-man lineup with five quick taps instead of opening the
  // modal five times.
  const [selected, setSelected] = useState(() => new Set());

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const commit = () => {
    if (selected.size === 0) {
      onClose();
      return;
    }
    const picks = available.filter((p) => selected.has(p.id));
    onPick(picks);
  };

  return (
    <div
      data-testid={`iqs-roster-picker-${color}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[85vh] rounded-xl border-2 border-gold/40 bg-navy-deep flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gold/20">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('iqs_pick_for')}</div>
            <div className="text-lg font-h text-white">
              {color === 'red' ? t('iqs_team_red') : t('iqs_team_black')}
            </div>
          </div>
          <button
            data-testid={`iqs-roster-picker-close-${color}`}
            onClick={onClose}
            className="p-2 rounded-md hover:bg-white/5 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-3 space-y-1.5 flex-1">
          {available.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">
              {roster.length === 0 ? t('iqs_no_roster') : t('iqs_no_players_left')}
            </div>
          ) : (
            available.map((p) => {
              const isSel = selected.has(p.id);
              return (
                <button
                  key={p.id}
                  data-testid={`iqs-roster-pick-${color}-${p.id}`}
                  onClick={() => toggle(p.id)}
                  aria-pressed={isSel}
                  className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-md border transition text-left ${
                    isSel
                      ? 'bg-gold/15 border-gold/60 ring-1 ring-gold/40'
                      : 'bg-black/30 hover:bg-gold/10 border-white/5 hover:border-gold/30'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        isSel ? 'bg-gold border-gold text-navy' : 'border-white/30'
                      }`}
                    >
                      {isSel && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${color === 'red' ? 'bg-red-700 text-white' : 'bg-slate-700 text-white'}`}>
                      {p.jersey_number ?? '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{p.name}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                        {p.position || '—'}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
        {available.length > 0 && (
          <div className="p-3 border-t border-gold/20 flex items-center justify-between gap-3 bg-black/30">
            <div className="text-[11px] font-mono text-slate-300">
              {selected.size > 0
                ? t('iqs_selected_count').replace('{n}', String(selected.size))
                : t('iqs_no_selection')}
            </div>
            <button
              data-testid={`iqs-roster-picker-commit-${color}`}
              onClick={commit}
              disabled={selected.size === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-gold text-navy font-bold text-xs uppercase tracking-widest hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-3.5 h-3.5" /> {t('iqs_add_selected')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Team panel (keypad) ──────────────────────────────────────────────
function TeamPanel({ color, team, target, rosterPlayers, t, onAction, onCombo, onAddPlayer, onRemovePlayer, locked, canEditRoster }) {
  const counts = useMemo(() => {
    const m = {};
    for (const a of team.actions) m[a.key] = (m[a.key] || 0) + 1;
    return m;
  }, [team.actions]);

  const efficiency = team.actions.length
    ? Math.round((team.points / team.actions.length) * 100)
    : 0;

  const sideBg = color === 'red'
    ? 'bg-gradient-to-b from-red-950/40 to-red-900/10 border-red-700/40'
    : 'bg-gradient-to-b from-slate-900/60 to-slate-950/80 border-slate-600/30';
  const sideAccent = color === 'red' ? 'text-red-400' : 'text-slate-200';
  const sideRing = color === 'red' ? 'ring-red-500/30' : 'ring-slate-400/20';

  return (
    <div
      data-testid={`iqs-team-${color}`}
      className={`rounded-xl border-2 p-4 sm:p-5 ${sideBg} ${locked ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className={`text-[10px] font-mono uppercase tracking-[0.32em] ${sideAccent}`}>
            {t('iqs_team')} · {color === 'red' ? t('iqs_team_red') : t('iqs_team_black')}
          </div>
          <div className="flex items-baseline gap-2 mt-1">
            <div className="text-5xl sm:text-6xl font-h text-white tabular-nums">{team.points.toFixed(1)}</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">/ {target}</div>
          </div>
        </div>
        <div className={`px-2.5 py-1 rounded-md bg-black/30 border border-white/10 text-[10px] font-mono uppercase tracking-[0.25em] ${sideAccent} ring-1 ${sideRing}`}>
          <div className="text-[9px] opacity-60">IQ EFF</div>
          <div className="text-sm text-white">{efficiency}%</div>
        </div>
      </div>

      {/* Roster chips */}
      <div className="mb-3 min-h-[2rem]">
        <div className="flex flex-wrap gap-1.5">
          {rosterPlayers.map((p) => (
            <span
              key={p.id}
              data-testid={`iqs-chip-${color}-${p.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${color === 'red' ? 'bg-red-800/60 border-red-500/40' : 'bg-slate-700/70 border-slate-400/30'} border text-white`}
            >
              <span className="font-mono text-[10px] opacity-80">#{p.jersey_number ?? '?'}</span>
              <span className="truncate max-w-[100px]">{p.name}</span>
              {canEditRoster && !locked && (
                <button
                  data-testid={`iqs-chip-remove-${color}-${p.id}`}
                  onClick={() => onRemovePlayer(p.id)}
                  className="ml-0.5 opacity-60 hover:opacity-100"
                  aria-label="Remove"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {canEditRoster && (
            <button
              data-testid={`iqs-add-player-${color}`}
              onClick={onAddPlayer}
              disabled={locked}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-dashed border-gold/40 text-gold/90 hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-3 h-3" /> {t('iqs_add_player')}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-black/40 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full transition-all duration-300 ${color === 'red' ? 'bg-red-500' : 'bg-slate-300'}`}
          style={{ width: `${Math.min(100, (team.points / target) * 100)}%` }}
        />
      </div>

      {/* Primary action keypad */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-2.5">
        {ACTIONS.map((a) => {
          const Icon = a.iconCmp;
          return (
            <button
              key={a.key}
              data-testid={`iqs-btn-${color}-${a.key}`}
              disabled={locked}
              onClick={() => onAction(a)}
              className={`relative group rounded-lg border bg-gradient-to-br ${COLOR_TO_TW[a.color]} p-2.5 sm:p-3 text-left transition-transform active:scale-95 hover:brightness-125 disabled:cursor-not-allowed`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <Icon className="w-4 h-4" />
                <span className="text-[10px] font-mono tabular-nums bg-black/40 px-1.5 py-0.5 rounded text-white">+{a.points}</span>
              </div>
              <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider leading-tight text-white/95">{t(a.labelKey)}</div>
              {counts[a.key] ? (
                <div className="absolute top-1 right-1 text-[9px] font-mono text-white/50 tabular-nums">×{counts[a.key]}</div>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Impact (combo) plays */}
      <div className="mt-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-gold/70 mb-1.5">
          {t('iqs_impact_plays')}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {COMBOS.map((c) => (
            <button
              key={c.key}
              data-testid={`iqs-combo-${color}-${c.key}`}
              disabled={locked}
              onClick={() => onCombo(c)}
              className="rounded-lg border border-gold/40 bg-gold/10 hover:bg-gold/20 active:scale-95 transition p-2 text-left disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between mb-0.5">
                <Activity className="w-3.5 h-3.5 text-gold" />
                <span className="text-[10px] font-mono tabular-nums bg-black/40 px-1.5 py-0.5 rounded text-gold">+{c.points}</span>
              </div>
              <div className="text-[10px] sm:text-[11px] font-semibold leading-tight text-white/95">{t(c.labelKey)}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Per-side stat breakdown (post-game) ──────────────────────────────
function StatBreakdown({ team, color, t }) {
  const counts = useMemo(() => {
    const m = {};
    for (const a of team.actions) m[a.key] = (m[a.key] || 0) + 1;
    return m;
  }, [team.actions]);
  const rows = [...ACTIONS, ...COMBOS].filter((row) => counts[row.key]);
  if (!rows.length) return null;
  return (
    <div className={`rounded-lg border p-3 ${color === 'red' ? 'border-red-500/30 bg-red-950/20' : 'border-slate-500/30 bg-slate-900/40'}`}>
      <div className={`text-[10px] font-mono uppercase tracking-[0.3em] mb-2 ${color === 'red' ? 'text-red-300' : 'text-slate-200'}`}>
        {color === 'red' ? t('iqs_team_red') : t('iqs_team_black')} · {t('iqs_breakdown')}
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center justify-between text-[11px] bg-black/30 rounded px-2 py-1">
            <span className="text-slate-300 truncate">{t(row.labelKey)}</span>
            <span className="font-mono text-white tabular-nums shrink-0 ml-2">
              ×{counts[row.key]} <span className="text-gold">·</span> +{(counts[row.key] * row.points).toFixed(1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Saved games list ────────────────────────────────────────────────
function SavedGamesList({ sessions, t }) {
  if (!sessions.length) return null;
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-black/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <HistoryIcon className="w-4 h-4 text-gold" />
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('iqs_history')}</div>
      </div>
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {sessions.map((s) => (
          <div
            key={s.id}
            data-testid={`iqs-history-row-${s.id}`}
            className="flex items-center justify-between gap-3 p-2 rounded-md bg-black/40 border border-white/5 text-xs"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="text-[10px] font-mono text-slate-400 shrink-0">
                {new Date(s.created_at).toLocaleDateString()}
              </div>
              <div className="text-[10px] font-mono text-gold/80 shrink-0">{s.mode}</div>
              {s.label && <div className="text-slate-200 truncate">{s.label}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-red-400 tabular-nums">{s.red?.points?.toFixed?.(1) ?? 0}</span>
              <span className="text-slate-400">vs</span>
              <span className="text-slate-200 tabular-nums">{s.black?.points?.toFixed?.(1) ?? 0}</span>
              {s.winner && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase bg-gold/20 text-gold ml-1">
                  {s.winner === 'red' ? t('iqs_team_red') : t('iqs_team_black')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────
export default function IQScore() {
  const { t } = useLang();
  const { user } = useAuth();
  const canManage = user?.role === 'coach' || user?.role === 'admin';

  const [mode, setMode] = useState('5v5');
  const [target, setTarget] = useState(20);
  const [red, setRed] = useState(initialTeam);
  const [black, setBlack] = useState(initialTeam);
  const [redRoster, setRedRoster] = useState([]); // string[]
  const [blackRoster, setBlackRoster] = useState([]); // string[]
  const [roster, setRoster] = useState([]); // full player objects
  const [pickerSide, setPickerSide] = useState(null); // 'red' | 'black' | null
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const historyRef = useRef([]);

  const winner = red.points >= target ? 'red'
    : black.points >= target ? 'black'
    : null;

  // Fetch roster + recent sessions once.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get('/roster');
        setRoster(r.data?.players || []);
      } catch { /* fine — non-coach or empty */ }
      if (canManage) {
        try {
          const s = await api.get('/iq-score/sessions?limit=10');
          setSessions(s.data?.sessions || []);
        } catch { /* non-fatal */ }
      }
    })();
  }, [user, canManage]);

  const push = (color, entry) => {
    const setter = color === 'red' ? setRed : setBlack;
    setter((prev) => ({
      points: +(prev.points + entry.points).toFixed(2),
      actions: [...prev.actions, entry],
    }));
    historyRef.current.push({ color, entry });
  };

  const handleAction = (color) => (a) => {
    if (winner) return;
    push(color, { key: a.key, points: a.points, ts: Date.now(), kind: 'single' });
  };
  const handleCombo = (color) => (c) => {
    if (winner) return;
    push(color, { key: c.key, points: c.points, ts: Date.now(), kind: 'combo' });
  };
  const handleUndo = () => {
    const last = historyRef.current.pop();
    if (!last) return;
    const setter = last.color === 'red' ? setRed : setBlack;
    setter((prev) => ({
      points: +(prev.points - last.entry.points).toFixed(2),
      actions: prev.actions.slice(0, -1),
    }));
  };
  const handleReset = () => {
    setRed(initialTeam());
    setBlack(initialTeam());
    historyRef.current = [];
    setLabel('');
  };

  // Roster handlers
  const handlePickPlayers = (players) => {
    const ids = players.map((p) => p.id);
    if (pickerSide === 'red') setRedRoster((r) => [...r, ...ids]);
    else if (pickerSide === 'black') setBlackRoster((r) => [...r, ...ids]);
    setPickerSide(null);
  };
  const handleRemoveRed = (id) => setRedRoster((r) => r.filter((x) => x !== id));
  const handleRemoveBlack = (id) => setBlackRoster((r) => r.filter((x) => x !== id));

  // Save handler
  const handleSave = async () => {
    if (!canManage) return;
    if (red.actions.length === 0 && black.actions.length === 0) {
      toast.error(t('iqs_save_empty'));
      return;
    }
    setSaving(true);
    try {
      const body = {
        mode,
        target,
        label: label.trim() || null,
        red_roster_ids: redRoster,
        black_roster_ids: blackRoster,
        red_actions: red.actions.map(({ key, points, kind, ts }) => ({ key, points, kind, ts })),
        black_actions: black.actions.map(({ key, points, kind, ts }) => ({ key, points, kind, ts })),
      };
      const r = await api.post('/iq-score/sessions', body);
      setSessions((prev) => [r.data, ...prev].slice(0, 10));
      toast.success(t('iqs_save_ok'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('iqs_save_fail'));
    } finally {
      setSaving(false);
    }
  };

  const totalActions = red.actions.length + black.actions.length;
  const balance = totalActions
    ? Math.round((Math.min(red.actions.length, black.actions.length) / Math.max(red.actions.length, black.actions.length)) * 100)
    : 100;

  const redChips = useMemo(
    () => redRoster.map((id) => roster.find((p) => p.id === id)).filter(Boolean),
    [redRoster, roster],
  );
  const blackChips = useMemo(
    () => blackRoster.map((id) => roster.find((p) => p.id === id)).filter(Boolean),
    [blackRoster, roster],
  );
  const takenIds = useMemo(
    () => new Set([...redRoster, ...blackRoster]),
    [redRoster, blackRoster],
  );

  const bannerRef = useRef(null);
  useEffect(() => {
    if (winner && bannerRef.current) {
      bannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [winner]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <BackToDashboard data-testid="iqs-back-dashboard" />

        <header className="mb-5">
          <div className="text-[11px] font-mono uppercase tracking-[0.32em] text-gold/80">{t('iqs_eyebrow')}</div>
          <h1 className="text-3xl sm:text-4xl font-h text-white mt-1">{t('iqs_title')}</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">{t('iqs_subtitle')}</p>
        </header>

        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex items-center gap-1.5 bg-black/30 border border-white/10 rounded-lg p-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 px-2">{t('iqs_mode')}</span>
            {MODES.map((m) => (
              <button
                key={m}
                data-testid={`iqs-mode-${m}`}
                onClick={() => setMode(m)}
                className={`text-[11px] font-mono px-2 py-1 rounded ${mode === m ? 'bg-gold text-navy font-bold' : 'text-slate-300 hover:bg-white/5'}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 bg-black/30 border border-white/10 rounded-lg p-1">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 px-2">{t('iqs_target')}</span>
            {TARGETS.map((p) => (
              <button
                key={p}
                data-testid={`iqs-target-${p}`}
                onClick={() => setTarget(p)}
                className={`text-[11px] font-mono px-2 py-1 rounded tabular-nums ${target === p ? 'bg-gold text-navy font-bold' : 'text-slate-300 hover:bg-white/5'}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="iqs-undo"
              onClick={handleUndo}
              disabled={historyRef.current.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest bg-slate-800 hover:bg-slate-700 text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Undo2 className="w-3.5 h-3.5" /> {t('iqs_undo')}
            </button>
            <button
              data-testid="iqs-reset"
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest bg-red-900/70 hover:bg-red-800 text-white"
            >
              <RotateCcw className="w-3.5 h-3.5" /> {t('iqs_reset')}
            </button>
          </div>
        </div>

        {/* Game label (optional) */}
        {canManage && (
          <div className="mb-5">
            <input
              data-testid="iqs-game-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('iqs_label_placeholder')}
              maxLength={120}
              className="w-full max-w-md bg-black/30 border border-white/10 focus:border-gold/40 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none"
            />
          </div>
        )}

        {/* Winner banner */}
        {winner && (
          <div
            ref={bannerRef}
            data-testid="iqs-winner-banner"
            className="mb-5 rounded-xl border-2 border-gold bg-gradient-to-r from-gold/20 via-gold/10 to-gold/20 p-4 flex flex-wrap items-center gap-3"
          >
            <Crown className="w-7 h-7 text-gold" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">{t('iqs_winner')}</div>
              <div className="text-xl font-h text-white">
                {winner === 'red' ? t('iqs_team_red') : t('iqs_team_black')} · {t('iqs_target_reached')} {target}
              </div>
            </div>
            {canManage && (
              <button
                data-testid="iqs-save-from-banner"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700 text-white font-bold text-xs uppercase tracking-widest hover:brightness-110 disabled:opacity-60"
              >
                <Save className="w-3.5 h-3.5" /> {saving ? t('iqs_saving') : t('iqs_save_game')}
              </button>
            )}
            <button
              onClick={handleReset}
              className="px-4 py-2 rounded-md bg-gold text-navy font-bold text-xs uppercase tracking-widest hover:brightness-110"
            >
              {t('iqs_new_game')}
            </button>
          </div>
        )}

        {/* Two-team keypad */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          <TeamPanel
            color="red"
            team={red}
            target={target}
            rosterPlayers={redChips}
            t={t}
            onAction={handleAction('red')}
            onCombo={handleCombo('red')}
            onAddPlayer={() => setPickerSide('red')}
            onRemovePlayer={handleRemoveRed}
            locked={!!winner}
            canEditRoster={canManage}
          />
          <TeamPanel
            color="black"
            team={black}
            target={target}
            rosterPlayers={blackChips}
            t={t}
            onAction={handleAction('black')}
            onCombo={handleCombo('black')}
            onAddPlayer={() => setPickerSide('black')}
            onRemovePlayer={handleRemoveBlack}
            locked={!!winner}
            canEditRoster={canManage}
          />
        </div>

        {/* Global stats strip */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <div className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('iqs_total_actions')}</div>
            <div className="text-2xl font-h text-white tabular-nums">{totalActions}</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('iqs_leader')}</div>
            <div className="text-2xl font-h text-white">
              {red.points === black.points ? '—' : red.points > black.points ? t('iqs_team_red') : t('iqs_team_black')}
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('iqs_balance')}</div>
            <div className="text-2xl font-h text-white tabular-nums">{balance}%</div>
          </div>
          <div className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('iqs_mode_active')}</div>
            <div className="text-2xl font-h text-white">{mode}</div>
          </div>
        </div>

        {/* Post-game stats */}
        {winner && (
          <div data-testid="iqs-postgame-stats" className="mt-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-gold" />
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('iqs_stats_title')}</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <StatBreakdown team={red} color="red" t={t} />
              <StatBreakdown team={black} color="black" t={t} />
            </div>
          </div>
        )}

        {/* Persistent save bar (when not yet won, coach can still snapshot) */}
        {canManage && !winner && totalActions > 0 && (
          <div className="mt-5 flex justify-end">
            <button
              data-testid="iqs-save-snapshot"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-700/80 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest disabled:opacity-60"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? t('iqs_saving') : t('iqs_save_snapshot')}
            </button>
          </div>
        )}

        {/* History */}
        {canManage && <SavedGamesList sessions={sessions} t={t} />}

        {/* Legend / rules */}
        <details className="mt-5 rounded-lg border border-white/10 bg-black/20 p-3 group">
          <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-[0.28em] text-gold/80 list-none flex items-center gap-2">
            <Trophy className="w-3.5 h-3.5" /> {t('iqs_rules_title')}
          </summary>
          <div className="mt-3 text-xs text-slate-300 space-y-1 leading-relaxed">
            <p>• {t('iqs_rules_basic')}</p>
            <p>• {t('iqs_rules_combos')}</p>
            <p>• {t('iqs_rules_target')}</p>
            <p>• {t('iqs_rules_purpose')}</p>
          </div>
        </details>
      </div>

      {/* Roster picker */}
      {pickerSide && (
        <RosterPicker
          color={pickerSide}
          roster={roster}
          takenIds={takenIds}
          onPick={handlePickPlayers}
          onClose={() => setPickerSide(null)}
          t={t}
        />
      )}
    </Layout>
  );
}
